import { LoadingButton } from '@mui/lab';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { FC, useState } from 'react';

interface Props {
  open: boolean;
  title: string;
  /** What the operator is about to trigger. */
  content: string;
  /** Shown as a warning banner — use it for the irreversible part, e.g. "mails every user". */
  warning?: string;
  confirmLabel?: string;
  onClose: () => void;
  /** Rejections are left to the caller so it can report them through `generalStore`. */
  onConfirm: () => Promise<void>;
}

/**
 * Generic "are you sure?" popup for actions that cannot be undone. Keeps the dialog open and the
 * confirm button in its loading state until `onConfirm` settles, then closes it.
 */
export const ConfirmDialog: FC<Props> = ({ open, title, content, warning, confirmLabel, onClose, onConfirm }) => {
  const [isLoading, setLoading] = useState(false);

  const confirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{content}</DialogContentText>
        {warning && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {warning}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={isLoading} onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton loading={isLoading} variant="contained" disableElevation color="primary" onClick={confirm}>
          {confirmLabel || 'Confirm'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
